/**
 * Reusable Forge-styled UI controls for settings panels.
 * Extracted from DesignTab.tsx for use across multiple tabs.
 *
 * Components:
 * - EffectSection: accordion with enable/disable toggle + reset button
 * - CollapsibleSection: simpler accordion (expand/collapse only)
 * - Slider: labeled range input
 * - ForgeToggle: styled checkbox
 * - ForgeSelect: custom dropdown
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import styles from './DesignTab.module.css';

// ---- EffectSection ----

interface EffectSectionProps {
  readonly name: string;
  readonly enabled: boolean;
  readonly onToggle: (enabled: boolean) => void;
  readonly onReset: () => void;
  readonly children?: React.ReactNode;
}

export function EffectSection({ name, enabled, onToggle, onReset, children }: EffectSectionProps) {
  const [expanded, setExpanded] = useState(enabled);

  const handleHeaderClick = useCallback(() => {
    setExpanded((v) => !v);
  }, []);

  const handleToggleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle(!enabled);
  }, [enabled, onToggle]);

  const handleResetClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onReset();
  }, [onReset]);

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader} onClick={handleHeaderClick}>
        <span className={styles.sectionTitle}>{name}</span>
        <div className={styles.sectionToggle}>
          <button type="button" className={styles.sectionResetButton} onClick={handleResetClick}>Reset</button>
          <span className={styles.toggleLabel}>{enabled ? 'ON' : 'OFF'}</span>
          <div
            className={enabled ? styles.toggleSwitchOn : styles.toggleSwitch}
            onClick={handleToggleClick}
          >
            <div className={enabled ? styles.toggleKnobOn : styles.toggleKnob} />
          </div>
        </div>
      </div>
      {expanded && children && (
        <div className={enabled ? styles.sectionBody : styles.sectionBodyDisabled}>
          {children}
        </div>
      )}
    </div>
  );
}

// ---- CollapsibleSection ----

interface CollapsibleSectionProps {
  readonly name: string;
  readonly defaultExpanded?: boolean;
  readonly children?: React.ReactNode;
}

export function CollapsibleSection({ name, defaultExpanded = false, children }: CollapsibleSectionProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const handleHeaderClick = useCallback(() => {
    setExpanded((v) => !v);
  }, []);

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeader} onClick={handleHeaderClick}>
        <span className={styles.sectionTitle}>{name}</span>
        <span className={expanded ? styles.sectionChevronOpen : styles.sectionChevron}>&#9654;</span>
      </div>
      {expanded && children && (
        <div className={styles.sectionBody}>
          {children}
        </div>
      )}
    </div>
  );
}

// ---- Slider ----

interface SliderProps {
  readonly label: string;
  readonly value: number;
  readonly min: number;
  readonly max: number;
  readonly step: number;
  readonly decimals?: number;
  readonly onChange: (value: number) => void;
}

export function Slider({ label, value, min, max, step, decimals = 2, onChange }: SliderProps) {
  return (
    <div className={styles.sliderRow}>
      <span className={styles.sliderLabel}>{label}</span>
      <input
        type="range"
        className={styles.sliderInput}
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
      />
      <span className={styles.sliderValue}>{value.toFixed(decimals)}</span>
    </div>
  );
}

// ---- ForgeToggle ----

interface ForgeToggleProps {
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (checked: boolean) => void;
}

export function ForgeToggle({ label, checked, onChange }: ForgeToggleProps) {
  const handleClick = useCallback(() => {
    onChange(!checked);
  }, [checked, onChange]);

  return (
    <div className={styles.checkboxRow} onClick={handleClick}>
      <input type="checkbox" checked={checked} readOnly />
      <div className={checked ? styles.toggleSwitchOn : styles.toggleSwitch}>
        <div className={checked ? styles.toggleKnobOn : styles.toggleKnob} />
      </div>
      <span>{label}</span>
    </div>
  );
}

// ---- ForgeSelect ----

interface ForgeSelectOption {
  label: string;
  value: string | number;
}

interface ForgeSelectProps {
  readonly label: string;
  readonly value: string | number;
  readonly options: ForgeSelectOption[];
  readonly onChange: (value: string) => void;
}

export function ForgeSelect({ label, value, options, onChange }: ForgeSelectProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const selectedLabel = options.find((o) => String(o.value) === String(value))?.label ?? '';

  const handleItemClick = useCallback((optValue: string | number) => {
    onChange(String(optValue));
    setOpen(false);
  }, [onChange]);

  const handleTriggerClick = useCallback(() => {
    setOpen((v) => !v);
  }, []);

  return (
    <div className={styles.selectRow}>
      <span className={styles.selectLabel}>{label}</span>
      <div className={styles.forgeSelectWrapper} ref={wrapperRef}>
        <button
          type="button"
          className={open ? styles.forgeSelectTriggerOpen : styles.forgeSelectTrigger}
          onClick={handleTriggerClick}
        >
          <span>{selectedLabel}</span>
          <span className={open ? styles.forgeSelectCaretOpen : styles.forgeSelectCaret}>&#9660;</span>
        </button>
        {open && (
          <div className={styles.forgeSelectMenu}>
            {options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={String(opt.value) === String(value) ? styles.forgeSelectItemActive : styles.forgeSelectItem}
                onClick={() => handleItemClick(opt.value)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
